// First-purchase offer ticket: reveals the docked ticket on desktop layouts, or
// shows it as a dialog where there is no room beside the page. It returns on
// every load of the homepage and shop; closing it lasts for that page view.
(() => {
  const dock = document.querySelector('[data-offer-ticket-dock]');
  const dialog = document.querySelector('[data-offer-ticket-dialog]');
  if (!dock || !dialog) return;
  const MIN_WIDTH = 150;
  const home = dock.dataset.placement === 'home';

  const tiltMedia = matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
  const tilt = face => {
    const reset = () => {
      face.classList.remove('is-tilting');
      for (const name of ['--obj-ticket-rx', '--obj-ticket-ry', '--obj-ticket-scale', '--obj-ticket-glare']) face.style.removeProperty(name);
    };
    face.addEventListener('pointermove', event => {
      if (!tiltMedia.matches || event.pointerType !== 'mouse') return;
      const box = face.getBoundingClientRect();
      const x = (event.clientX - box.left) / box.width, y = (event.clientY - box.top) / box.height;
      face.classList.add('is-tilting');
      face.style.setProperty('--obj-ticket-rx', `${((.5 - y) * 12).toFixed(2)}deg`);
      face.style.setProperty('--obj-ticket-ry', `${((x - .5) * 12).toFixed(2)}deg`);
      face.style.setProperty('--obj-ticket-scale', '1.02');
      face.style.setProperty('--obj-ticket-gx', `${(x * 100).toFixed(1)}%`);
      face.style.setProperty('--obj-ticket-gy', `${(y * 100).toFixed(1)}%`);
      face.style.setProperty('--obj-ticket-glare', '1');
    });
    face.addEventListener('pointerleave', reset);
  };
  document.querySelectorAll('[data-offer-ticket-face]').forEach(tilt);

  // The docked ticket needs a desktop layout and enough room beside the page.
  const docked = () => {
    dock.hidden = false;
    const slot = dock.querySelector('.obj-ticket-dock__slot');
    const fits = getComputedStyle(dock).display !== 'none' && slot.getBoundingClientRect().width >= MIN_WIDTH;
    dock.toggleAttribute('data-too-narrow', !fits);
    dock.hidden = !fits;
    return fits;
  };

  let dismissed = false;
  const close = () => {
    dismissed = true;
    dock.classList.remove('is-visible');
    dock.hidden = true;
  };
  dock.querySelector('[data-offer-ticket-close]')?.addEventListener('click', close);
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  dialog.addEventListener('close', () => { dismissed = true; });

  // Home: wait for the loading screen, then keep the ticket off the opening
  // wordmark and the footer.
  const loaderGone = () => !document.querySelector('[data-objectiv-loader]')?.open;
  const whenReady = callback => {
    if (!home || loaderGone()) { callback(); return; }
    const timer = setInterval(() => { if (loaderGone()) { clearInterval(timer); callback(); } }, 250);
  };
  const watchHome = () => {
    const hero = document.querySelector('[data-objectiv-logo-hero]');
    const footer = document.querySelector('[class*="shopify-section-group-footer-group"]');
    const state = { hero: !!hero, footer: false };
    const update = () => dock.classList.toggle('is-visible', !state.hero && !state.footer);
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) state[entry.target === hero ? 'hero' : 'footer'] = entry.isIntersecting;
      update();
    }, { threshold: 0 });
    if (hero) observer.observe(hero);
    if (footer) observer.observe(footer);
    update();
  };

  // The logo is letterboxed inside its row; lift the intro so it sits midway
  // between the drawn logo and the first offer line.
  const centreIntro = () => document.querySelectorAll('.obj-ticket').forEach(ticket => {
    const pattern = ticket.querySelector('.obj-ticket__pattern'), drawn = pattern?.querySelector('g'), intro = ticket.querySelector('.obj-ticket__intro');
    if (!drawn || !intro) return;
    const box = pattern.getBoundingClientRect(), logo = drawn.getBoundingClientRect();
    if (box.height) intro.style.translate = `0 ${-Math.max(0, box.bottom - logo.bottom) / 2}px`;
  });

  // On the homepage, Shop Now takes the reader to the first claim (section 2);
  // desktops keep the link to the shop page.
  const desktop = matchMedia('(min-width: 1101px) and (pointer: fine)');
  if (home) document.querySelectorAll('a.obj-ticket__cta').forEach(link => link.addEventListener('click', event => {
    const claims = document.querySelector('.obj-scrolly-section');
    if (!claims || desktop.matches) return;
    event.preventDefault();
    if (dialog.open) dialog.close();
    const scroll = window.ObjectivScroll;
    (scroll || window).scrollTo({top: (scroll?.y ?? scrollY) + claims.getBoundingClientRect().top, behavior: 'instant'});
  }));

  const phone = matchMedia('(max-width: 749px)');
  let popupTimer = 0, watching = false;
  const reveal = () => {
    if (!home) dock.classList.add('is-visible');
    else if (!watching) { watching = true; watchHome(); }
    requestAnimationFrame(centreIntro);
  };
  // Wait for a quiet moment with no other dialog, drawer or menu open.
  const popup = () => {
    clearTimeout(popupTimer);
    popupTimer = setTimeout(() => {
      if (dialog.open || dismissed) return;
      if (docked()) { reveal(); return; }
      if (document.hidden || document.querySelector('dialog[open], cart-drawer.active, .menu-opening')) { popup(); return; }
      // Shop phones show a compact landscape ticket above the browser bar that
      // leaves the page usable; elsewhere the modal ticket stays.
      if (phone.matches && !home) dialog.show(); else dialog.showModal();
      centreIntro();
    }, phone.matches ? 1000 : 2000);
  };
  const place = () => { if (docked()) reveal(); else popup(); };

  whenReady(() => {
    place();
    // Back/forward navigation restores the page from memory: offer it again.
    addEventListener('pageshow', event => {
      if (!event.persisted) return;
      dismissed = false;
      place();
    });
    let resizeFrame = 0;
    addEventListener('resize', () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => { if (!dialog.open && !dismissed) place(); centreIntro(); });
    });
  });
})();
